import {DiscoverySettings} from '../../aee/sharing/DiscoverySettings';
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAccount } from "../../auth/AuthProvider";
import { authApi } from "../../aee/api";
import type { LinkedIdentity } from "../../../worker/shared/authContracts";
import { ProfileForm } from "../../auth/ProfileForm";
import "../../auth/auth.css";
import { JournalAppBar } from "./JournalAppShell";
import "./NotebookPage.css";
import "./JournalAppShell.css";
import "./ProfilePage.css";
export function ProfilePage() {
  const { user } = useAccount();
  const location = useLocation();
  useEffect(() => {
    if (location.hash)
      document
        .getElementById(location.hash.slice(1))
        ?.scrollIntoView?.({ block: "start" });
  }, [location.hash]);
  const [identities, setIdentities] = useState<LinkedIdentity[] | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    try {
      setIdentities((await authApi.identities()).identities);
      setError("");
    } catch {
      setError("Unable to load linked accounts. Please try again.");
    }
  }, []);
  useEffect(() => {
    void Promise.resolve().then(refresh);
  }, [refresh]);
  return (
    <section className="notebook nb-app nb-light nb-account-app">
      <JournalAppBar environmentHome notice="" />
      <main className="aee-account-scroll">
        <div className="aee-account-layout">
          <header className="aee-account-heading">
            <Link to="/app">← Engineering Environment Home</Link>
            <h1>Your account</h1>
          </header>
          <nav className="aee-account-nav" aria-label="Account settings">
            <a href="#profile">Profile</a>
            <a href="#linked">Linked Accounts</a>
            <a href="#settings">Settings</a>
          </nav>
          <div className="aee-account-panels">
            <section id="profile" className="aee-account-panel">
              <header className="aee-account-identity">
                <span className="aee-account-avatar" aria-hidden="true">
                  {user?.profile
                    ? `${user.profile.firstName[0]}${user.profile.lastName[0]}`.toUpperCase()
                    : (user?.displayName || "A")
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((name) => name[0])
                        .join("")
                        .toUpperCase()}
                </span>
                <div>
                  <h2>{user?.displayName || "Aldapa account"}</h2>
                  <p>Personal profile</p>
                </div>
              </header>
              <ProfileForm /><DiscoverySettings />
            </section>
            <section id="linked" className="aee-account-panel">
              <h2>Linked Accounts</h2>
              <p className="aee-account-description">
                Manage the providers you use to sign in.
              </p>
              {error && (
                <p role="alert">
                  {error} <button onClick={() => void refresh()}>Retry</button>
                </p>
              )}
              {!identities ? (
                <p role="status">Loading providers…</p>
              ) : (
                (["google", "github"] as const).map((provider) => {
                  const linked = identities.filter(
                    (identity) => identity.provider === provider,
                  );
                  return (
                    <div className="aee-provider-row" key={provider}>
                      <span className="aee-provider-mark" aria-hidden="true">
                        {provider === "google" ? "G" : "⌘"}
                      </span>
                      <div>
                        <h3>{provider === "google" ? "Google" : "GitHub"}</h3>
                        <p>
                          {linked.length
                            ? `Connected · ${linked.map((identity) => identity.email).join(", ")}`
                            : "Not connected"}
                        </p>
                      </div>
                      {linked.length ? (
                        <button
                          disabled={
                            busy ||
                            new Set(
                              identities.map((identity) => identity.provider),
                            ).size < 2
                          }
                          onClick={async () => {
                            if (!confirm(`Disconnect ${provider}?`)) return;
                            setBusy(true);
                            try {
                              await authApi.unlinkProvider(provider);
                              await refresh();
                            } catch (failure) {
                              setError(
                                failure instanceof Error
                                  ? failure.message
                                  : "Unable to disconnect.",
                              );
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Disconnect
                        </button>
                      ) : (
                        <a
                          className="aee-account-button"
                          href={authApi.linkProviderUrl(provider)}
                        >
                          Connect →
                        </a>
                      )}
                    </div>
                  );
                })
              )}
              <p className="aee-account-help">
                Keep at least one connected provider to access your account.
              </p>
            </section>
            <section id="settings" className="aee-account-panel">
              <h2>Settings</h2>
              <p className="aee-account-description">
                Changes save automatically after editing. Use Save or Ctrl/Cmd+S
                to save immediately.
              </p>
            </section>
          </div>
        </div>
      </main>
    </section>
  );
}
